import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  getAuthMe,
  getLoginUrl,
  postLogout,
  postMagicLinkRequest,
  postMagicLinkVerify,
  authEvents,
  type AuthUser,
} from '../api.ts';

/** Result from requestMagicLink — undefined means success. */
export interface MagicLinkRequestError {
  message: string;
  /** HTTP status code from the server (e.g. 429 for cooldown). */
  status?: number;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  /**
   * Request a magic-link + 6-digit code for `email`.
   * Returns an error object on failure (including `status` for 429), or
   * undefined on success.
   */
  requestMagicLink: (email: string) => Promise<MagicLinkRequestError | undefined>;
  /**
   * Verify the 6-digit code. The token id is held in the httpOnly `ml_pending`
   * cookie — JS never handles it. On success, fetches the session user and sets
   * the user state (same mechanism as Foundation login via BroadcastChannel).
   * Returns an error string on failure, or undefined on success.
   */
  verifyCode: (code: string) => Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  login: () => {},
  logout: () => {},
  requestMagicLink: async () => ({ message: 'Not initialized' }),
  verifyCode: async () => undefined,
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Check existing session on mount
  useEffect(() => {
    getAuthMe()
      .then(setUser)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Listen for 401s from API calls
  useEffect(() => {
    const handler = () => setUser(null);
    authEvents.addEventListener('unauthenticated', handler);
    return () => authEvents.removeEventListener('unauthenticated', handler);
  }, []);

  // Listen for auth result from popup via BroadcastChannel
  // (window.opener is nullified by COOP headers after cross-origin navigation)
  useEffect(() => {
    const ch = new BroadcastChannel('auth');
    ch.onmessage = (event) => {
      if (event.data?.type === 'auth-success' && event.data.user) {
        setUser(event.data.user);
      }
    };
    return () => ch.close();
  }, []);

  const login = useCallback(() => {
    getLoginUrl()
      .then((url) => {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        window.open(url, 'auth-popup', `width=${width},height=${height},left=${left},top=${top}`);
      })
      .catch(() => {});
  }, []);

  const logout = useCallback(() => {
    postLogout()
      .then(() => setUser(null))
      .catch(() => {});
  }, []);

  // Magic-link: request a sign-in link + code for the given email. Capture the
  // current deep-link path so the email-link sign-in returns here (the in-tab
  // code path already preserves the URL). Server sanitises `next`.
  const requestMagicLink = useCallback(async (email: string): Promise<MagicLinkRequestError | undefined> => {
    const result = await postMagicLinkRequest(email, window.location.pathname);
    if (!result.ok) {
      return { message: result.error ?? 'Failed to send sign-in link', status: result.status };
    }
    return undefined;
  }, []);

  // Magic-link: verify the 6-digit code. The token id lives in the httpOnly
  // `ml_pending` cookie — JS never sees or forwards it. On success, refresh the
  // user from /auth/me exactly as `getAuthMe()`-on-mount does for link-landing.
  const verifyCode = useCallback(async (code: string): Promise<string | undefined> => {
    const result = await postMagicLinkVerify(code);
    if (!result.ok) return result.error ?? 'Invalid code';
    // Session cookie is now set — pull the user identity from /auth/me.
    try {
      const me = await getAuthMe();
      setUser(me);
    } catch {
      // Unexpected: verify succeeded but /auth/me failed. Let the mount-effect
      // handle it on the next render cycle, surface an error to the caller.
      return 'Sign-in succeeded but could not load your profile — please refresh';
    }
    return undefined;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, requestMagicLink, verifyCode }}>
      {children}
    </AuthContext.Provider>
  );
}
